import { BadgeInfo, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@ui/card.tsx';
import { Switch } from '@ui/switch.tsx';
import { Label } from '@ui/label.tsx';
import { Separator } from '@ui/separator.tsx';
import { adminWorkspaceConfigs } from '@r/api/generated/client.ts';
import type { WorkspaceConfigWorkspaceConfigReadModel } from '@r/api/generated/client.schemas.ts';
import { useAllConfigs, findConfig, getConfigsFromResponse } from '@r/hooks/use-all-configs.ts';
import { useGetConfigByType } from '@r/hooks/use-workspace-config.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useHideableWorkspaceConfigSwitch } from '@admin/hooks/use-hideable-workspace-config-switch.ts';

const CONFIG_TYPE_CORE = 'core';
const CONFIG_KEY_HIDE_SLIDER_TITLE = 'hide-slider-title';
const CONFIG_KEY_HIDE_ORDER_SHORTCUT = 'hide-order-shortcut';

export const PersonalizationDisplayPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();

  const allConfigsQuery = useAllConfigs();
  const availableConfigs = getConfigsFromResponse(allConfigsQuery.data);

  const hideSliderConfigDef = findConfig(
    availableConfigs,
    CONFIG_TYPE_CORE,
    CONFIG_KEY_HIDE_SLIDER_TITLE
  );
  const hideOrderShortcutConfigDef = findConfig(
    availableConfigs,
    CONFIG_TYPE_CORE,
    CONFIG_KEY_HIDE_ORDER_SHORTCUT
  );

  const hideSliderConfigIri = hideSliderConfigDef?.id ? `/configs/${hideSliderConfigDef.id}` : null;
  const hideOrderShortcutConfigIri = hideOrderShortcutConfigDef?.id
    ? `/configs/${hideOrderShortcutConfigDef.id}`
    : null;

  const hideSliderRaw = useGetConfigByType(CONFIG_TYPE_CORE, CONFIG_KEY_HIDE_SLIDER_TITLE);
  const isSliderHidden = hideSliderRaw?.[0] === 'true';

  const hideOrderShortcutRaw = useGetConfigByType(CONFIG_TYPE_CORE, CONFIG_KEY_HIDE_ORDER_SHORTCUT);
  const isOrderShortcutHidden = hideOrderShortcutRaw?.[0] === 'true';

  const workspaceConfigsQuery = useQuery<WorkspaceConfigWorkspaceConfigReadModel[]>({
    queryKey: ['workspaceConfigs', 'personalization-display'],
    queryFn: async () => {
      const response = await adminWorkspaceConfigs({
        'config.type': CONFIG_TYPE_CORE,
        'config.key[]': [CONFIG_KEY_HIDE_SLIDER_TITLE, CONFIG_KEY_HIDE_ORDER_SHORTCUT]
      });
      return (response.data ?? []) as WorkspaceConfigWorkspaceConfigReadModel[];
    },
    enabled: Boolean(hideSliderConfigIri || hideOrderShortcutConfigIri)
  });

  const hideSliderSwitch = useHideableWorkspaceConfigSwitch({
    configType: CONFIG_TYPE_CORE,
    configKey: CONFIG_KEY_HIDE_SLIDER_TITLE,
    configIri: hideSliderConfigIri,
    workspaceConfigsData: workspaceConfigsQuery.data,
    isDiscoveryLoading: workspaceConfigsQuery.isLoading,
    currentValue: isSliderHidden,
    queryClient,
    t,
    showOrvalErrorToast
  });

  const hideOrderShortcutSwitch = useHideableWorkspaceConfigSwitch({
    configType: CONFIG_TYPE_CORE,
    configKey: CONFIG_KEY_HIDE_ORDER_SHORTCUT,
    configIri: hideOrderShortcutConfigIri,
    workspaceConfigsData: workspaceConfigsQuery.data,
    isDiscoveryLoading: workspaceConfigsQuery.isLoading,
    currentValue: isOrderShortcutHidden,
    queryClient,
    t,
    showOrvalErrorToast
  });

  const handleShowSliderChange = (checked: boolean) => {
    hideSliderSwitch.toggle(!checked, { isActivated: checked });
  };

  const handleShowOrderShortcutChange = (checked: boolean) => {
    hideOrderShortcutSwitch.toggle(!checked, { isActivated: checked });
  };

  return (
    <div className="flex flex-col space-y-10 grow">
      <div className="space-y-3">
        <h1 className="text-4xl font-bold">{t('personalization.display.title')}</h1>
        <p className="font-light text-muted-foreground">
          {t('personalization.display.description')}
        </p>
      </div>

      <Card className="w-full">
        <CardContent className="p-6 space-y-8">
          <div className="space-y-4">
            <div className="flex gap-3 items-center">
              <SlidersHorizontal className="w-5 h-5 shrink-0 text-foreground" />
              <h2 className="text-xl font-semibold">
                {t('personalization.display.section.title')}
              </h2>
            </div>
            <p className="text-muted-foreground">{t('personalization.display.section.intro')}</p>
            <p className="text-muted-foreground">
              {t('personalization.display.section.intro.secondary')}
            </p>
            <div className="flex gap-2 items-start text-muted-foreground">
              <BadgeInfo className="mt-0.5 w-5 h-5 shrink-0 stroke-info" />
              <p>{t('personalization.display.section.info')}</p>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('personalization.display.slider.title')}</h3>
            <p className="text-muted-foreground">
              {t('personalization.display.slider.description')}
            </p>
            <div className="flex items-center space-x-2">
              <Switch
                id="show-slider-title"
                checked={!hideSliderSwitch.value}
                disabled={!hideSliderSwitch.isReady || hideSliderSwitch.isUpdating}
                onCheckedChange={handleShowSliderChange}
              />
              <Label htmlFor="show-slider-title" className="mb-0 font-normal">
                {t('personalization.display.slider.toggle.label')}
              </Label>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {t('personalization.display.order.shortcut.title')}
            </h3>
            <p className="text-muted-foreground">
              {t('personalization.display.order.shortcut.description')}
            </p>
            <div className="flex items-center space-x-2">
              <Switch
                id="show-order-shortcut"
                checked={!hideOrderShortcutSwitch.value}
                disabled={!hideOrderShortcutSwitch.isReady || hideOrderShortcutSwitch.isUpdating}
                onCheckedChange={handleShowOrderShortcutChange}
              />
              <Label htmlFor="show-order-shortcut" className="mb-0 font-normal">
                {t('personalization.display.order.shortcut.toggle.label')}
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
